function integerIds(value) {
  const values = Array.isArray(value) ? value : [value];
  return values
    .map((item) => Number(item))
    .filter((item) => Number.isInteger(item) && item > 0);
}

function lookupByStudentId(lookup, studentId) {
  if (lookup instanceof Map) return lookup.get(Number(studentId));
  return lookup?.[Number(studentId)] || null;
}

function eventTimestamp(event) {
  const timestamp = new Date(event?.timestamp).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

/**
 * Describe where a child should go for a pickup-zone arrival. Bus drivers are
 * labelled by the child's effective bus, while every other pickup uses the
 * arriving contact's name.
 */
export function getPickupDestination(event, studentId, busByStudentId = new Map()) {
  const contactId = Number(event?.parent_driver_id);
  if (!Number.isInteger(contactId) || contactId <= 0) return null;

  const bus = lookupByStudentId(busByStudentId, studentId);
  const busDriverId = Number(bus?.driver_contact_id);
  const busName = String(bus?.name || '').trim();
  if (busName && busDriverId === contactId) {
    return {
      type: 'bus',
      name: busName,
      label: `Going home on ${busName}`,
    };
  }

  const contactName = String(event?.parent_name || '').trim();
  if (!contactName) return null;
  return {
    type: 'contact',
    name: contactName,
    label: `Going home with ${contactName}`,
  };
}

export function getDismissalStatusDestination(status) {
  const name = String(status?.called_for_name || '').trim();
  if (!name) return null;

  const type = status?.called_for_type === 'bus' ? 'bus' : 'contact';
  return {
    type,
    name,
    label: type === 'bus' ? `Going home on ${name}` : `Going home with ${name}`,
  };
}

/**
 * Rebuild the called-card destinations after a refresh. The current geofence
 * feed contains each in-zone contact and the students covered by that arrival;
 * choose the arrival nearest to the time each child was called.
 */
export function buildCalledPickupDestinations({
  dismissalStatuses = [],
  geofenceEvents = [],
  busByStudentId = new Map(),
} = {}) {
  const destinations = {};
  const enteringEvents = geofenceEvents.filter((event) => event?.action === 'enter');

  dismissalStatuses
    .filter((status) => String(status?.status || '').toLowerCase() === 'called')
    .forEach((status) => {
      const studentId = Number(status.student_id);
      if (!Number.isInteger(studentId) || studentId <= 0) return;

      const savedDestination = getDismissalStatusDestination(status);
      if (savedDestination) {
        destinations[studentId] = savedDestination;
        return;
      }

      const candidates = enteringEvents.filter((event) => (
        integerIds(event.children_ids).includes(studentId)
      ));
      if (!candidates.length) return;

      const calledAt = new Date(status.called_at).getTime();
      candidates.sort((left, right) => {
        if (Number.isFinite(calledAt)) {
          const distance = Math.abs(eventTimestamp(left) - calledAt)
            - Math.abs(eventTimestamp(right) - calledAt);
          if (distance !== 0) return distance;
        }
        return eventTimestamp(right) - eventTimestamp(left);
      });

      const destination = getPickupDestination(candidates[0], studentId, busByStudentId);
      if (destination) destinations[studentId] = destination;
    });

  return destinations;
}
